import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Lock, Library, Headphones, Video, FileText, Type, Dumbbell } from "lucide-react";

const FORMATS = { audio: Headphones, video: Video, pdf: FileText, texto: Type, exercicio: Dumbbell };
const empty = { title: "", description: "", category: "", format: "audio", url: "", body_text: "", is_premium: false, price: 0 };

export default function AdminContent() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/content").then(({ data }) => setList(data));
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, category: cats[0]?.name || "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    if (editing) await api.put(`/content/${editing.id}`, payload);
    else await api.post("/content", payload);
    toast.success("Conteúdo salvo"); setOpen(false); load();
  };
  const del = async (id) => { await api.delete(`/content/${id}`); toast.success("Removido"); load(); };

  return (
    <div>
      <PageHeader title="Biblioteca de Conteúdos" subtitle="Gerencie os conteúdos da plataforma"
        action={<Button onClick={openNew} className="rounded-full" data-testid="new-content-btn"><Plus className="w-4 h-4 mr-1" />Novo conteúdo</Button>} />
      {list.length === 0 ? <EmptyState icon={Library} title="Nenhum conteúdo" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => {
            const Icon = FORMATS[c.format] || Type;
            return (
              <Card key={c.id} className="p-5" data-testid={`content-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                  {c.is_premium && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"><Lock className="w-3 h-3 mr-1" />Premium</Badge>}
                </div>
                <h3 className="font-medium mt-3">{c.title}</h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{c.description}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground"><Badge variant="secondary">{c.category}</Badge><span>{c.format}</span>{c.is_premium && <span>· R$ {c.price?.toFixed(2)}</span>}</div>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(c)}><Pencil className="w-4 h-4 mr-1" />Editar</Button>
                  <Button variant="outline" size="sm" className="text-destructive" onClick={() => del(c.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar conteúdo" : "Novo conteúdo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="content-title-input" className="mt-1" /></div>
            <div><Label>Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="content-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Formato</Label>
                <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["audio", "video", "pdf", "texto", "exercicio"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Link (áudio/vídeo/PDF)</Label><Input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} className="mt-1" placeholder="https://" /></div>
            <div><Label>Texto (para conteúdo em texto)</Label><Textarea value={form.body_text || ""} onChange={(e) => setForm({ ...form, body_text: e.target.value })} className="mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_premium} onCheckedChange={(v) => setForm({ ...form, is_premium: v })} data-testid="content-premium-switch" /><Label>Conteúdo Premium (bloqueado)</Label></div>
            {form.is_premium && <div><Label>Preço (R$)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" /></div>}
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-content-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
